import { Box, Typography } from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { ReactNode, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputHelperText from "../../../../common/components/functional/error/InputHelperText";
import AutocompleteBase from "../../../../common/components/functional/fields/autocompleteBase/AutocompleteBase";
import ExpertsAutocomplete from "../../../../common/components/functional/fields/expertsAutocomplete/ExpertsAutocomplete";
import { SearchFilter } from "../../../../common/components/functional/FilterSearchField/FilterSearchField";
import { RoundedButton } from "../../../../common/components/styled/styled";
import {
  leadingZerosDateFormat,
  validateDateInput,
} from "../../../../common/helpers/formatDateTime";
import { NameIdOption } from "../../../../common/types/commonTypes";
import {
  buildHrTaskListFilters,
  EMPTY_HR_TASK_LIST_FILTER_FORM,
  HrTaskFilterCategory,
  HrTaskListFilterFormValues,
  parseHrTaskListFilters,
} from "./common";

type Props = {
  filters: SearchFilter[];
  updateFilters: (filters: SearchFilter[]) => void;
  handleMenuToggle: () => void;
  categories: readonly HrTaskFilterCategory[];
};

const FilterField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const HrTaskListFilters = ({
  filters,
  updateFilters,
  handleMenuToggle,
  categories,
}: Props) => {
  const { t } = useTranslation(["dashboard", "common", "errors"]);
  const methods = useForm<HrTaskListFilterFormValues>({
    defaultValues: parseHrTaskListFilters(
      filters,
      categories.map((category) => category.id),
    ),
  });
  const { control, getValues, reset, watch } = methods;
  const createdRange =
    watch("createdRange") ?? EMPTY_HR_TASK_LIST_FILTER_FORM.createdRange;

  useEffect(() => {
    reset(
      parseHrTaskListFilters(
        filters,
        categories.map((category) => category.id),
      ),
    );
  }, [categories, filters, reset]);

  const categoryOptions: NameIdOption[] = categories.map((category) => ({
    id: category.id,
    name: t(`hrTasklist.filters.categories.${category.id}`),
  }));

  const categoryLabel = (categoryId: string) =>
    t(`hrTasklist.filters.categories.${categoryId}`);

  const sendFilters = () => {
    handleMenuToggle();
    updateFilters(buildHrTaskListFilters(getValues(), categoryLabel));
  };

  const hasRangeError = !!(
    createdRange.from &&
    createdRange.to &&
    createdRange.from > createdRange.to
  );
  const hasInvalidDate =
    (createdRange.from !== null &&
      !validateDateInput(createdRange.from, true)) ||
    (createdRange.to !== null && !validateDateInput(createdRange.to, true));
  const hasErrors = hasRangeError || hasInvalidDate;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pt: 1,
        width: "100%",
        minWidth: 0,
      }}
    >
      <FilterField label={t("hrTasklist.filters.created")}>
        <Controller
          name="createdRange"
          control={control}
          render={({ field }) => (
            <DateRangePicker
              format={leadingZerosDateFormat}
              value={[field.value.from, field.value.to]}
              onChange={(newValue) => {
                field.onChange({
                  from: newValue?.[0] ?? null,
                  to: newValue?.[1] ?? null,
                });
              }}
              minDate={new Date(1970, 0, 1)}
              maxDate={new Date(2099, 11, 31)}
              slotProps={{
                textField: ({ position }) => ({
                  size: "small",
                  placeholder:
                    position === "start"
                      ? t("hrTasklist.filters.dateFrom")
                      : t("hrTasklist.filters.dateTo"),
                  label: null,
                  sx: { flex: 1, minWidth: 0 },
                }),
              }}
              sx={{
                width: "100%",
                minWidth: 0,
                "& .MuiTextField-root": { minWidth: 0, flex: 1 },
              }}
            />
          )}
        />
        <InputHelperText
          errorMessage={
            hasRangeError
              ? t("errors:startDateMustBeBeforeEndDate")
              : hasInvalidDate
                ? t("common:invalidDate")
                : undefined
          }
        />
      </FilterField>
      <FilterField label={t("hrTasklist.filters.taskType")}>
        <Controller
          name="categories"
          control={control}
          render={({ field }) => (
            <AutocompleteBase<NameIdOption>
              value={categoryOptions.filter((option) =>
                field.value.includes(option.id),
              )}
              onChange={(selected) =>
                field.onChange((selected ?? []).map((option) => option.id))
              }
              options={categoryOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              multiple
              fullWidth
              limitTags={1}
            />
          )}
        />
      </FilterField>
      <FilterField label={t("hrTasklist.filters.handler")}>
        <Controller
          name="experts"
          control={control}
          render={({ field }) => (
            <ExpertsAutocomplete
              value={field.value}
              onChange={(experts) => field.onChange(experts ?? [])}
              limitTags={1}
            />
          )}
        />
      </FilterField>
      <Box>
        <RoundedButton
          variant="contained"
          onClick={sendFilters}
          disabled={hasErrors}
        >
          {t("common:search")}
        </RoundedButton>
      </Box>
    </Box>
  );
};

export default HrTaskListFilters;
